import React, { useEffect, useMemo, useState } from "react";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import { observer } from "mobx-react-lite";
import moment from "moment";
import { useFormContext } from "./Form";
import { withFormWrapper, DEFAULT_ID } from "./withFormWrapper";
import { StaticInputValue } from "./StaticInput";
import { useLocale } from "./hooks/useLocale";
import "moment/dist/locale/en-gb";
import "moment/dist/locale/fr";
import "moment/dist/locale/nl";
import { range } from "lodash";
import { useDebounce } from "./utils/useDebounce";
// ** TODOs **
/*
  - Improve the UX of the component when the user is typing in a value. Would be good to provide feedback it's correct.
*/
export const FORMATS = {
    datetime: "YYYY-MM-DDTHH:mm:ss",
    date: "YYYY-MM-DD",
    monthYear: "MM/YY",
    time: "HH:mm:ss",
    year: "YYYY",
};
export const RawDateTimeInput = observer(({ name, onChange, type = "datetime", disabled, input = true, renderDay, renderView, renderInput, open = undefined, isValidDate, placeholder, uniqueId = DEFAULT_ID, label, staticFormatter, localeOverride, useBrowserLocale = false, dataId, dataLabel, dataTestId, timeConstraints, ariaLabelledby, onInputStatusChange, }) => {
    const { values, formDisabled, setIsDirty, isStatic } = useFormContext();
    const [invalidStringValue, setInvalidStringValue] = useState();
    const localeFromContext = useLocale();
    const [momentValue, setMomentValue] = useState();
    const debouncedMomentValue = useDebounce(momentValue, 1000);
    useEffect(() => {
        if (values[name]) {
            const momentValue = getValidMomentByType(values[name], type);
            if (momentValue) {
                setInvalidStringValue(undefined);
                if (onInputStatusChange) {
                    onInputStatusChange(false);
                }
            }
        }
    }, [values, name, type, onInputStatusChange]);
    const locale = useMemo(() => localeOverride ||
        (useBrowserLocale ? window.navigator.language : localeFromContext), [localeOverride, useBrowserLocale, localeFromContext]);
    const localeSet = Boolean(localeOverride || useBrowserLocale || localeFromContext);
    useEffect(() => {
        if (locale && locale === "en-US") {
            // This requires the meridiem to be "am" or "pm" case insensitive instead of allowing single letter "A" or "P".
            // This prevents the annoying autocomplete when you type "A," which results in "AMM" when you still type "M".
            moment.updateLocale("en", {
                meridiemParse: /[ap]\.?m\.?/i,
            });
        }
    }, [locale]);
    const dateFormat = useMemo(() => {
        let dateFormat = "MM/DD/YYYY";
        let timeFormat = "HH:mm A";
        if (localeSet) {
            dateFormat = moment().locale(locale).localeData().longDateFormat("L");
            timeFormat = moment()
                .locale(locale)
                .localeData()
                .longDateFormat("LT");
        }
        const monthYearFormat = "MM/YY";
        const yearFormat = "YYYY";
        if (type === "datetime")
            return `${dateFormat} ${timeFormat}`;
        if (type === "time")
            return timeFormat;
        if (type === "monthYear")
            return monthYearFormat;
        if (type === "year")
            return yearFormat;
        return dateFormat;
    }, [type, localeSet, locale]);
    const formattedDateFormat = useMemo(() => {
        let formatted = dateFormat.toUpperCase();
        if (formatted.includes("A") && !formatted.includes("AM")) {
            formatted = formatted.replace("A", "AM");
        }
        return formatted;
    }, [dateFormat]);
    const getValidMomentByType = (value, type) => {
        let momentValue;
        if (moment.isMoment(value)) {
            momentValue = value.locale(locale);
        }
        else {
            const singleDigitDateFormat = dateFormat
                .replace("MM", "M")
                .replace("DD", "D")
                .replace("HH", "H");
            const formats = [
                FORMATS[type],
                dateFormat,
                singleDigitDateFormat,
            ];
            // only add the no space format (1:00pm) if the type includes a time and use a 12 hour clock
            if (type.includes("time") && singleDigitDateFormat.includes("A")) {
                const noSpaceTimeFormat = singleDigitDateFormat
                    .replace("h:mm A", "h:mmA")
                    .replace("H:mm A", "H:mmA");
                formats.push(noSpaceTimeFormat);
            }
            momentValue = moment(value, formats, true).locale(locale);
        }
        if (momentValue.isValid())
            return momentValue;
    };
    const roundToTimeConstraints = (value) => {
        if (!timeConstraints)
            return value;
        if (!!timeConstraints["hours"]) {
            value.set("hour", getNearestConstraintValue(timeConstraints["hours"], value.hour()));
        }
        if (!!timeConstraints["minutes"]) {
            value.set("minute", getNearestConstraintValue(timeConstraints["minutes"], value.minute()));
        }
        return value;
    };
    const getNearestConstraintValue = (constraint, value) => {
        const allowedValues = range(constraint["min"], constraint["max"] + 1, constraint["step"]);
        const nearestValue = allowedValues.reduce(function (prev, curr) {
            return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
        });
        return nearestValue;
    };
    useEffect(() => {
        if (debouncedMomentValue && !!timeConstraints) {
            const roundedValue = roundToTimeConstraints(debouncedMomentValue);
            handleChange(roundedValue);
        }
    }, [debouncedMomentValue]);
    const handleChange = React.useCallback((selected) => {
        const oldValue = values.get(name);
        const momentValue = getValidMomentByType(selected, type);
        setIsDirty(true);
        setMomentValue(momentValue);
        // if the given or parsed moment is a valid Moment object,
        // update the form value with formatted string and emit the change
        if (momentValue) {
            const newValue = momentValue.format(FORMATS[type]);
            values.set(name, newValue);
            setInvalidStringValue(undefined);
            handleOnChange(newValue, oldValue, onChange);
            if (onInputStatusChange) {
                onInputStatusChange(false);
            }
        }
        // if the value is a string that is not a valid moment,
        // display that string and set the form value to undefined since there is no valid value set
        // this allows users to type in values
        else if (typeof selected === "string" && selected.trim() !== "") {
            setInvalidStringValue(selected);
            values.set(name, undefined);
            handleOnChange(undefined, oldValue, onChange);
            if (onInputStatusChange) {
                onInputStatusChange(true);
            }
        }
        else {
            values.set(name, undefined);
            handleOnChange(undefined, oldValue, onChange);
            if (onInputStatusChange) {
                onInputStatusChange(false);
            }
        }
    }, [setIsDirty, values, name, type, onChange]);
    const handleOnChange = (newValue, oldValue, onChange) => {
        if (onChange && newValue !== oldValue) {
            onChange(newValue, oldValue);
        }
    };
    if (isStatic) {
        return (React.createElement(StaticInputValue, { label: label, name: name, staticFormatter: staticFormatter, dataId: dataId, dataLabel: dataLabel, dataTestId: dataTestId }));
    }
    const getValidDateFormat = () => {
        if (type === "time")
            return false;
        if (["datetime", "date"].indexOf(type) !== -1) {
            // Returning true here yields the standard datepicker, since we don't care
            // about the format we show to the user
            return true;
        }
        return FORMATS[type];
    };
    const getFormValue = () => {
        const value = values.get(name);
        if (!value) {
            return undefined;
        }
        return moment.utc(value, FORMATS[type]).locale(locale);
    };
    const extraInputProps = {};
    if (!invalidStringValue && !values.get(name)) {
        extraInputProps["value"] = "";
    }
    return (React.createElement("div", null,
        React.createElement("span", { className: "sr-only" },
            "Date format: ",
            dateFormat,
            " "),
        React.createElement(Datetime, { dateFormat: getValidDateFormat(), timeFormat: ["datetime", "time"].indexOf(type) !== -1, value: invalidStringValue || getFormValue(), onChange: handleChange, renderDay: renderDay, renderView: renderView, renderInput: renderInput, closeOnSelect: true, locale: locale, utc: true, inputProps: {
                disabled: disabled || formDisabled,
                id: ariaLabelledby ? undefined : `form-group-${name}-${uniqueId}`,
                placeholder: placeholder ? placeholder : formattedDateFormat,
                autoComplete: "on",
                "aria-labelledby": ariaLabelledby,
                ...extraInputProps,
            }, input: input, open: open, isValidDate: isValidDate, "data-id": dataId ? `${dataId}-picker` : undefined, "data-label": dataLabel ? `${dataLabel}-picker` : undefined, "data-testid": dataTestId ? `${dataTestId}-picker` : undefined, timeConstraints: timeConstraints }),
        (values.get(name) || invalidStringValue) && (React.createElement("div", { className: "rbt-aux" },
            React.createElement("button", { "aria-label": "Clear", className: "close rbt-close", type: "button", onClick: () => {
                    handleChange("");
                    setInvalidStringValue(undefined);
                }, "data-id": dataId ? `${dataId}-clear` : undefined, "data-label": dataLabel ? `${dataLabel}-clear` : undefined, "data-testid": dataTestId ? `${dataTestId}-clear` : undefined },
                React.createElement("svg", { height: "20", width: "20", viewBox: "0 0 20 20", "aria-hidden": "true", focusable: "false" },
                    React.createElement("path", { d: "M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" })),
                React.createElement("span", { className: "sr-only" }, "Clear Date"))))));
});
export const DateTimeInput = withFormWrapper({
    WrappedComponent: RawDateTimeInput,
});
